import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import apiClient from '../../../lib/api';
import { UserEvents, enableFullTracking } from '../../../lib/analytics';
import {
  LoadingSpinner,
  ErrorMessage,
  PageContainer,
  ContentCard,
  ContentCardTitle,
  ContentCardDescription,
  ContentCardHeaderSection,
  PrimaryButton,
  TextButton,
} from '../../../components';

/**
 * 通信の秘密同意画面（電気通信事業法 第4条 / LINE アカウント単位・Web 取得）。
 *
 * 設計書: docs/pre-design/communication-secret-consent/design.md §8.2
 * - 説明文・version・hash は BE から取得（GET /api/consent/communication-secret/document）
 * - checkbox は div + onClick 実装（LIFF WebView の change double-fire 対策 / 旧 Consent.tsx 由来）
 * - チェック文言は「上記の情報の取り扱いに同意する」に統一（通信当事者本人が同意する前提のため
 *   「本人に説明し、同意を得ました」のような代理説明分岐は持たない）
 * - 送信時は BE から取得した hash・version をそのまま POST。BE が再算出・照合する。
 *   PP / 利用規約 URL は consent_document_text にリンクとして埋め込まれており、
 *   hash 照合でその一致も担保されるため個別フィールドとしては送信しない。
 * - 保存成否は onComplete(success, consentDate) で親に通知
 */

interface Document {
  version_major: number;
  version_minor: number;
  consent_document_text: string;
  consent_document_hash: string;
}

export interface CommunicationSecretConsentProps {
  /**
   * 保存成功後、親側で遷移処理を行うためのコールバック。
   * `consentDate` に加え、保存された `versionMajor` / `versionMinor` を渡し、
   * 親側で status クエリのキャッシュを楽観更新できるようにする（無限リダイレクトループ防止 /
   * PR-3 レビュー指摘）。
   */
  onComplete: (result: {
    consentDate: string;
    versionMajor: number;
    versionMinor: number;
  }) => void | Promise<void>;
  /** 「戻る」ボタン押下時のハンドラ。無指定なら戻るボタンは非表示 */
  onCancel?: () => void;
}

// 本画面の見出しブロックは中央寄せ
const HeaderSection = styled(ContentCardHeaderSection)`
  text-align: center;
`;

const ScrollableContent = styled.div`
  max-height: 50vh;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.background};
  -webkit-overflow-scrolling: touch;
`;

const DocumentText = styled.pre`
  margin: 0;
  font-family: inherit;
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 150%;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

// GlobalStyles の `button, a { display: inline-flex; min-width/min-height: 44px }`
// （タッチターゲット確保）を上書きする。inline-flex のままだとリンクが分割不可の
// 塊として行頭に折り返され、min-height で行間も広がり文中リンクにならない。
// 文章中のインラインリンクは WCAG 2.5.8 Target Size の inline 例外に該当するため
// タッチターゲット最小値の対象外。
const InlineLink = styled.a`
  display: inline;
  min-width: 0;
  min-height: 0;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: underline;
  word-break: break-all;
`;

// Figma独自値（旧 Consent.tsx と同一）: 同意チェックの背景色
const CONSENT_CHECK_BG = '#FEF3C7';

const ConsentCheckbox = styled.div`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  height: 55px;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.ms};
  background-color: ${CONSENT_CHECK_BG};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-user-select: none;
  touch-action: manipulation;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

const CheckboxLabel = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  line-height: 150%;
  color: ${({ theme }) => theme.colors.text.primary};
`;

// ネイティブ checkbox の代わりに span で見た目を描画（LIFF WebView 互換）。
const CheckboxBox = styled.span<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: 4px;
  border: 2px solid
    ${({ theme, $checked }) =>
      $checked ? theme.colors.primary : theme.colors.border.medium};
  background-color: ${({ theme, $checked }) =>
    $checked ? theme.colors.primary : theme.colors.background};
  color: ${({ theme }) => theme.colors.text.inverse};
  font-size: 16px;
  line-height: 1;
  transition: background-color 0.15s, border-color 0.15s;
`;

const ErrorText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.backgroundTableDanger};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border-left: 4px solid ${({ theme }) => theme.colors.status.error};
`;

// URL のスキーム検証（PR-3 レビュー指摘）。BE は現状 shared 定数の literal URL を
// 返すが、将来動的化された場合の javascript: 等スキームによる自己 XSS 経路を構造的に
// 閉じる。検証失敗時は null を返しリンクをプレーンテキストのまま表示する（同意保存経路は
// BE 側でも SSOT と照合して弾くため、機能的な破綻はない）。
const toSafeUrl = (url: string, fieldName: string): string | null => {
  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.toString();
    }
    console.warn(`${fieldName} has unsupported scheme; link hidden:`, parsed.protocol);
    return null;
  } catch {
    console.warn(`${fieldName} is not a valid URL; link hidden`);
    return null;
  }
};

// consent_document_text 内の軽量マークアップ（**太字** / [ラベル](URL) / 生 URL の自動
// リンク化）を React ノードに変換する。対応記法はこの 3 種のみに限定し、汎用 Markdown
// パーサーは導入しない（consent_documents.py の文面がこの 3 種のみを使う前提）。
const INLINE_MARKUP_PATTERN =
  /\*\*(.+?)\*\*|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s)]+)/g;

const renderConsentDocumentText = (text: string): React.ReactNode => {
  const lines = text.split('\n');
  return lines.map((line, lineIndex) => {
    const nodes: React.ReactNode[] = [];
    let lastIndex = 0;
    let partIndex = 0;
    INLINE_MARKUP_PATTERN.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = INLINE_MARKUP_PATTERN.exec(line)) !== null) {
      if (match.index > lastIndex) {
        nodes.push(line.slice(lastIndex, match.index));
      }
      const [full, boldText, linkLabel, linkUrl, autoUrl] = match;
      if (boldText !== undefined) {
        nodes.push(<strong key={`b-${lineIndex}-${partIndex++}`}>{boldText}</strong>);
      } else if (linkLabel !== undefined) {
        const safeUrl = toSafeUrl(linkUrl, 'consent_document_text inline link');
        nodes.push(
          safeUrl ? (
            <InlineLink
              key={`l-${lineIndex}-${partIndex++}`}
              href={safeUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {linkLabel}
            </InlineLink>
          ) : (
            linkLabel
          ),
        );
      } else if (autoUrl !== undefined) {
        const safeUrl = toSafeUrl(autoUrl, 'consent_document_text inline link');
        nodes.push(
          safeUrl ? (
            <InlineLink
              key={`l-${lineIndex}-${partIndex++}`}
              href={safeUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {autoUrl}
            </InlineLink>
          ) : (
            autoUrl
          ),
        );
      }
      lastIndex = match.index + full.length;
    }
    if (lastIndex < line.length) {
      nodes.push(line.slice(lastIndex));
    }
    return (
      <React.Fragment key={`line-${lineIndex}`}>
        {nodes}
        {lineIndex < lines.length - 1 ? '\n' : null}
      </React.Fragment>
    );
  });
};

export const CommunicationSecretConsent: React.FC<CommunicationSecretConsentProps> = ({
  onComplete,
  onCancel,
}) => {
  const [doc, setDoc] = useState<Document | null>(null);
  const [isFetchingDoc, setIsFetchingDoc] = useState(true);
  const [docError, setDocError] = useState<string>('');
  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');

  // 説明文・version・hash を BE から取得
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsFetchingDoc(true);
      setDocError('');
      try {
        const data = await apiClient.getCommunicationSecretConsentDocument();
        if (!cancelled) {
          setDoc(data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('同意文面の取得に失敗しました:', err);
          setDocError(
            '同意文面の取得に失敗しました。しばらく待ってから再度お試しください。',
          );
        }
      } finally {
        if (!cancelled) {
          setIsFetchingDoc(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggleAgreed = () => {
    setAgreed((prev) => !prev);
  };

  const handleCheckboxKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      handleToggleAgreed();
    }
  };

  const handleSubmit = async () => {
    if (!agreed || !doc) return;
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const result = await apiClient.submitCommunicationSecretConsent({
        version_major: doc.version_major,
        version_minor: doc.version_minor,
        consent_document_hash: doc.consent_document_hash,
      });

      // Analytics: 同意取得イベント & GA4 初期化
      UserEvents.consentGiven(`${doc.version_major}.${doc.version_minor}`);
      enableFullTracking();

      await onComplete({
        consentDate: result.consent_date,
        versionMajor: result.version_major,
        versionMinor: result.version_minor,
      });
      // 遷移完了に任せて isSubmitting は true のまま維持（旧 Consent.tsx と同型）
    } catch (err: unknown) {
      // BE のエラーメッセージは英語の技術的文言（"Consent version mismatch..." 等）
      // のため、error_code を見て日本語 UX に変換する（PR-3 レビュー指摘）。
      // 高齢者向け介護施設家族アプリの法的同意フローが英語表示のまま行き詰まらない
      // ようにするための必須処理。
      const errObj = err as {
        message?: string;
        details?: { error_code?: string };
      };
      const errorCode = errObj?.details?.error_code;

      if (errorCode === 'CONSENT_VERSION_MISMATCH') {
        // 同意対象バージョンが更新された場合の再同意誘導。doc を再取得して
        // agreed をリセットし、ユーザーが最新版を確認した上で再送信できるようにする。
        setSubmitError('同意内容が更新されました。もう一度ご確認のうえ同意してください。');
        setAgreed(false);
        setIsSubmitting(false);
        try {
          const fresh = await apiClient.getCommunicationSecretConsentDocument();
          setDoc(fresh);
        } catch (refetchErr) {
          console.error('同意文面の再取得に失敗しました:', refetchErr);
          // doc の再取得に失敗しても、既存の doc / チェックオフのままエラー表示は残る
        }
        return;
      }

      const messageByCode: Record<string, string> = {
        CONSENT_DOCUMENT_MISMATCH:
          '同意内容が更新されました。画面を再読み込みして最新の内容をご確認のうえ、同意してください。',
        CONSENT_TIMESTAMP_CONFLICT:
          'ほぼ同時に別のリクエストが処理されたようです。恐れ入りますが、もう一度同意ボタンを押してください。',
      };
      const fallback = '同意の保存に失敗しました。しばらく待ってから再度お試しください。';
      const localizedMessage = errorCode && messageByCode[errorCode]
        ? messageByCode[errorCode]
        : fallback;

      if (import.meta.env.DEV && errObj?.message) {
        // 開発時のみ BE の英語メッセージをコンソールに残す（本番 UI には出さない）
        console.warn(
          `[CommunicationSecretConsent] submit failed (${errorCode ?? 'no code'}):`,
          errObj.message,
        );
      }
      setSubmitError(localizedMessage);
      setIsSubmitting(false);
    }
  };

  if (isFetchingDoc) {
    return <LoadingSpinner fullPage message="同意文面を取得中..." />;
  }

  if (docError || !doc) {
    return (
      <ErrorMessage
        fullPage
        title="同意文面の取得に失敗しました"
        description={docError || '不明なエラーが発生しました。'}
        onRetry={() => window.location.reload()}
        retryLabel="再読み込み"
      />
    );
  }

  if (isSubmitting) {
    return <LoadingSpinner fullPage message="処理中..." />;
  }

  const isSubmitDisabled = !agreed || isSubmitting;

  return (
    <PageContainer>
      <ContentCard>
        <HeaderSection>
          <ContentCardTitle>Rehab スマホ連絡帳</ContentCardTitle>
          <ContentCardDescription>情報の取り扱いについて</ContentCardDescription>
        </HeaderSection>

        <ScrollableContent>
          <DocumentText>{renderConsentDocumentText(doc.consent_document_text)}</DocumentText>
        </ScrollableContent>

        <ConsentCheckbox
          role="checkbox"
          aria-checked={agreed}
          tabIndex={0}
          onClick={handleToggleAgreed}
          onKeyDown={handleCheckboxKeyDown}
        >
          <CheckboxBox $checked={agreed} aria-hidden="true">
            {agreed ? '✓' : ''}
          </CheckboxBox>
          <CheckboxLabel>上記の情報の取り扱いに同意する</CheckboxLabel>
        </ConsentCheckbox>

        {submitError && <ErrorText>{submitError}</ErrorText>}

        <PrimaryButton onClick={handleSubmit} disabled={isSubmitDisabled}>
          同意して利用を開始する
        </PrimaryButton>

        {onCancel && <TextButton onClick={onCancel}>戻る</TextButton>}
      </ContentCard>
    </PageContainer>
  );
};

export default CommunicationSecretConsent;
